/**
 * Home's default view: every item grouped by type. Vertical stacks
 * collapsible groups of full-width rows (status, title, client and visa,
 * property chips, time, date, assignee); Horizontal lays the non-empty types
 * out as agenda columns (⌘B toggles). Display picks the visible properties.
 */
import { CircleCheck } from "lucide-react";
import { useState, type FC } from "react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { EmptyState } from "@/components/ui/empty-state";
import { initials } from "@/components/ui/inbox-row";
import type { Kind } from "@/components/ui/kind-icon";
import type { HomeLayout } from "@/components/ui/layout-toggle";
import { StatusBadge } from "@/components/ui/status-badge";
import { Tag } from "@/components/ui/tag";
import {
  ListGroupHeader,
  TypeColumn,
  TypeListRow
} from "@/components/ui/type-list";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";

import {
  DEFAULT_HOME_PROPERTIES,
  type HomeProperties
} from "../dashboardLayoutStore";
import { chipLabel, groupByKind, homeKindOf, statusOf } from "./homeKinds";
import { dueDay, rightLabel, type TriageItem } from "./triageModel";

const groupId = (kind: Kind) => `home-group-${kind}`;

const DOT: Record<Kind, string> = {
  deadline: "bg-kind-deadline",
  expiration: "bg-kind-expiration",
  agent: "bg-kind-agent",
  intake: "bg-kind-intake",
  calendar: "bg-kind-calendar"
};

const contextOf = (t: TriageItem) =>
  [t.clientName, t.caseLabel].filter(Boolean).join(" · ");

const When: FC<{ item: TriageItem; now: Date }> = ({ item, now }) => {
  const label = rightLabel(item, now);
  return item.overdue && label ? (
    <StatusBadge tone="danger">{label}</StatusBadge>
  ) : (
    <span>{label}</span>
  );
};

const TypeChip: FC<{ item: TriageItem }> = ({ item }) => (
  <Tag data-testid="type-chip">
    <span
      aria-hidden
      className={`size-1.5 shrink-0 rounded-full ${DOT[homeKindOf(item)]}`}
    />
    {chipLabel(item)}
  </Tag>
);

/** Who the item is on: its first assignee, else you for your approvals. */
export const assigneeOf = (
  item: TriageItem,
  meName: string | null
): string | null =>
  item.assignees[0] ?? (item.assignedToMe && meName ? meName : null);

const Assignee: FC<{ name: string; more: number }> = ({ name, more }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span data-testid="row-assignee">
        <Avatar size="2xs">
          <AvatarFallback>{initials(name) || "?"}</AvatarFallback>
        </Avatar>
      </span>
    </TooltipTrigger>
    <TooltipContent side="left">
      {more ? `${name} + ${more} more` : name}
    </TooltipContent>
  </Tooltip>
);

/** A plain Horizontal row: when and date, the title, then client. */
const ColumnRow: FC<{ item: TriageItem; now: Date; onClick: () => void }> = ({
  item,
  now,
  onClick
}) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`by-type-card-${item.id}`}
    className="flex w-full min-w-0 flex-col gap-0.5 border-t border-line-1 px-3 py-2.5 text-left outline-none hover:bg-fill-1 focus-visible:bg-fill-1"
  >
    <span className="flex min-h-5 items-center gap-1.5 text-sm text-fg-3 tabular-nums">
      <When item={item} now={now} />
      {item.due ? (
        <span className="text-fg-2">{dueDay(item.due).format("MMM D")}</span>
      ) : null}
    </span>
    <span className="line-clamp-2 text-base text-fg">{item.title}</span>
    <span className="truncate text-sm text-fg-3">{contextOf(item)}</span>
  </button>
);

type Props = {
  items: TriageItem[];
  layout: HomeLayout;
  onLayoutChange: (layout: HomeLayout) => void;
  onOpen: (id: string) => void;
  properties?: HomeProperties;
  /** Your name, for the assignee on approvals and mentions. */
  meName?: string | null;
  now?: Date;
};

export const ByTypeView: FC<Props> = ({
  items,
  layout,
  onLayoutChange,
  onOpen,
  properties = DEFAULT_HOME_PROPERTIES,
  meName = null,
  now = new Date()
}) => {
  const [collapsed, setCollapsed] = useState<ReadonlySet<Kind>>(new Set());
  const groups = groupByKind(items).filter((g) => g.rows.length > 0);

  if (items.length === 0) {
    return (
      <EmptyState
        icon={CircleCheck}
        title="You're All Caught Up"
        description="New deadlines, approvals and intakes land here."
        className="flex-1"
        data-testid="by-type-empty"
      />
    );
  }

  if (layout === "horizontal") {
    const openVertical = (kind: Kind) => {
      onLayoutChange("vertical");
      setCollapsed((s) => {
        const next = new Set(s);
        next.delete(kind);
        return next;
      });
      window.requestAnimationFrame(() =>
        document.getElementById(groupId(kind))?.scrollIntoView()
      );
    };
    return (
      <div
        className="grid min-h-0 flex-1 divide-x divide-line-2"
        style={{
          gridTemplateColumns: `repeat(${groups.length}, minmax(0, 1fr))`
        }}
        data-testid="by-type-horizontal"
      >
        {groups.map((g) => (
          <TypeColumn
            key={g.kind}
            kind={g.kind}
            count={g.rows.length}
            onOpenVertical={() => openVertical(g.kind)}
            className="[&>header]:border-b-0"
          >
            {g.rows.map((t) => (
              <ColumnRow
                key={t.id}
                item={t}
                now={now}
                onClick={() => onOpen(t.id)}
              />
            ))}
          </TypeColumn>
        ))}
      </div>
    );
  }

  const toggle = (kind: Kind) =>
    setCollapsed((s) => {
      const next = new Set(s);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });

  return (
    <div
      className="min-h-0 flex-1 overflow-y-auto"
      data-testid="by-type-vertical"
    >
      {groups.map((g) => {
        const open = !collapsed.has(g.kind);
        return (
          <section key={g.kind} id={groupId(g.kind)}>
            <ListGroupHeader
              kind={g.kind}
              count={g.rows.length}
              open={open}
              onClick={() => toggle(g.kind)}
              className="sticky top-0 z-10"
            />
            {open
              ? g.rows.map((t) => {
                  const who = properties.assignee
                    ? assigneeOf(t, meName)
                    : null;
                  return (
                    <TypeListRow
                      key={t.id}
                      status={statusOf(t)}
                      title={t.title}
                      context={properties.context ? contextOf(t) : undefined}
                      chips={
                        properties.chips ? <TypeChip item={t} /> : undefined
                      }
                      when={<When item={t} now={now} />}
                      date={
                        properties.date && t.due
                          ? dueDay(t.due).format("MMM D")
                          : null
                      }
                      assignee={
                        who ? (
                          <Assignee
                            name={who}
                            more={Math.max(0, t.assignees.length - 1)}
                          />
                        ) : null
                      }
                      onClick={() => onOpen(t.id)}
                      data-testid={`by-type-row-${t.id}`}
                    />
                  );
                })
              : null}
          </section>
        );
      })}
    </div>
  );
};
